import {lazy} from 'react';

import {createRoute, layoutComponent, withRouter} from 'client/routeHelpers';

const AssignmentBuilderLayoutWithRequiredSession = lazy(() =>
  import('./AssignmentBuilderLayout.react').then((module) => ({
    default: module.AssignmentBuilderLayoutWithRequiredSession
  }))
);

const AssignmentSettings = lazy(() =>
  import('./AssignmentSettings/AssignmentSettings.react').then((module) => ({
    default: module.AssignmentSettings
  }))
);

const AssignmentSharingV3 = lazy(() =>
  import('./Document/AssignmentSharing/AssignmentSharingV3.react').then((module) => ({
    default: module.AssignmentSharing
  }))
);

const DocumentContentDiscovery = lazy(
  () => import('./Document/DocumentContentDiscovery/DocumentContentDiscovery.react')
);

const ReviewQuestions = lazy(() => import('./ViewAssignment/ViewAssignment.react'));

/**
 * `/assignment-builder/create-assignment` — the assignment builder layout
 * (session-gated) wrapping the content-discovery, settings, and sharing leaves.
 * Mirrors the RR3 layout + IndexRoute + nested settings/confirm routes 1:1; the
 * RR3 settings/confirm parents (IndexRoute + `draft` child, same component) are
 * flattened into two leaf routes each so the URLs stay identical.
 */
export function makeAssignmentBuilderRoute(parentRoute) {
  const top = createRoute({
    getParentRoute: () => parentRoute,
    path: 'assignment-builder/create-assignment',
    component: layoutComponent(AssignmentBuilderLayoutWithRequiredSession)
  });

  return top.addChildren([
    // Index
    createRoute({
      getParentRoute: () => top,
      path: '/',
      component: withRouter(DocumentContentDiscovery)
    }),

    // Create routes
    createRoute({
      getParentRoute: () => top,
      path: 'settings',
      component: withRouter(AssignmentSettings)
    }),
    createRoute({
      getParentRoute: () => top,
      path: 'classroom/$classroomId/settings',
      component: withRouter(AssignmentSettings)
    }),
    createRoute({
      getParentRoute: () => top,
      path: 'classroom/$classroomId/student/$studentId/settings',
      component: withRouter(AssignmentSettings)
    }),
    createRoute({
      getParentRoute: () => top,
      path: 'confirm',
      component: withRouter(AssignmentSharingV3)
    }),

    // Edit routes
    createRoute({
      getParentRoute: () => top,
      path: 'folder/$folderId',
      component: withRouter(DocumentContentDiscovery)
    }),
    createRoute({
      getParentRoute: () => top,
      path: '$assignmentId',
      component: withRouter(ReviewQuestions)
    }),
    createRoute({
      getParentRoute: () => top,
      path: '$assignmentId/draft',
      component: withRouter(DocumentContentDiscovery)
    }),

    // $assignmentId/settings (index + draft → two leaves, same component)
    createRoute({
      getParentRoute: () => top,
      path: '$assignmentId/settings',
      component: withRouter(AssignmentSettings)
    }),
    createRoute({
      getParentRoute: () => top,
      path: '$assignmentId/settings/draft',
      component: withRouter(AssignmentSettings)
    }),

    // $assignmentId/classroom/$classroomId/settings (index + draft)
    createRoute({
      getParentRoute: () => top,
      path: '$assignmentId/classroom/$classroomId/settings',
      component: withRouter(AssignmentSettings)
    }),
    createRoute({
      getParentRoute: () => top,
      path: '$assignmentId/classroom/$classroomId/settings/draft',
      component: withRouter(AssignmentSettings)
    }),

    // $assignmentId/classroom/$classroomId/student/$studentId/settings (index + draft)
    createRoute({
      getParentRoute: () => top,
      path: '$assignmentId/classroom/$classroomId/student/$studentId/settings',
      component: withRouter(AssignmentSettings)
    }),
    createRoute({
      getParentRoute: () => top,
      path: '$assignmentId/classroom/$classroomId/student/$studentId/settings/draft',
      component: withRouter(AssignmentSettings)
    }),

    // $assignmentId/confirm (index + draft → two leaves, AssignmentSharingV3)
    createRoute({
      getParentRoute: () => top,
      path: '$assignmentId/confirm',
      component: withRouter(AssignmentSharingV3)
    }),
    createRoute({
      getParentRoute: () => top,
      path: '$assignmentId/confirm/draft',
      component: withRouter(AssignmentSharingV3)
    })
  ]);
}
